const MILLIONS_MULTIPLIER = 1_000_000;

export function parseFinancialNumber(value) {
  if (value === null || value === undefined || value === '') return null;
  const parsed = parseFloat(String(value).replace(/[$,%]/g, '').replaceAll(',', ''));
  return Number.isNaN(parsed) ? null : parsed;
}

function getActiveEquitySummaryMethod(equitySummary) {
  if (!equitySummary) return null;
  return equitySummary.goalSeek ?? equitySummary.backsolve ?? equitySummary.pubco ?? equitySummary.mna ?? null;
}

export function getConcludedTotalValue(calcData, gridData) {
  const concluded = calcData?.concluded ?? gridData?.defaultScenarios?.concluded;
  if (!concluded) return null;

  const directTotal = parseFinancialNumber(concluded.totalValue);
  if (directTotal !== null) return directTotal;

  const totalValues = concluded.object?.['Total Value'];
  if (!Array.isArray(totalValues) || totalValues.length === 0) return null;

  return totalValues.reduce(
    (sum, value) => sum + (parseFinancialNumber(value) ?? 0),
    0,
  );
}

export function getCashAndEquivalents(clientFinancialInfo, equitySummary) {
  const cash = parseFinancialNumber(clientFinancialInfo?.currentCash?.value);
  if (cash !== null) return cash;

  const summaryMethod = getActiveEquitySummaryMethod(equitySummary);
  return parseFinancialNumber(
    summaryMethod?.['Plus: accrued interest, cash, and equivalents'],
  );
}

export function getTotalDebt(clientFinancialInfo, equitySummary) {
  const totalDebt = parseFinancialNumber(clientFinancialInfo?.totalDebt?.value);
  const convertibleDebt = parseFinancialNumber(clientFinancialInfo?.convertibleDebt?.value) ?? 0;
  const nonConvertibleDebt = parseFinancialNumber(clientFinancialInfo?.nonConvertibleDebt?.value) ?? 0;
  const combinedDebt = convertibleDebt + nonConvertibleDebt;

  if (totalDebt !== null) {
    return Math.max(totalDebt, combinedDebt);
  }

  if (combinedDebt > 0) return combinedDebt;

  const summaryMethod = getActiveEquitySummaryMethod(equitySummary);
  return parseFinancialNumber(summaryMethod?.['Less: debt']);
}

export function getEnterpriseValue(marketCapitalization, cashAndEquivalents, totalDebt) {
  if (marketCapitalization === null) return null;
  const cash = cashAndEquivalents ?? 0;
  const debt = totalDebt ?? 0;
  return marketCapitalization - cash + debt;
}

export function buildSubjectCompanyMetrics({
  calcData,
  gridData,
  transactionData,
  equitySummary,
}) {
  const clientFinancialInfo = transactionData?.clientFinancialInfo ?? {};
  const marketCapitalization = getConcludedTotalValue(calcData, gridData);
  const cashAndEquivalents = getCashAndEquivalents(clientFinancialInfo, equitySummary);
  const totalDebt = getTotalDebt(clientFinancialInfo, equitySummary);

  return {
    marketCapitalization,
    cashAndEquivalents,
    totalDebt,
    enterpriseValue: getEnterpriseValue(marketCapitalization, cashAndEquivalents, totalDebt),
  };
}

export { MILLIONS_MULTIPLIER };
